import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  Grid,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import LocalGasStationIcon from '@mui/icons-material/LocalGasStation';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import SpeedIcon from '@mui/icons-material/Speed';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import DownloadIcon from '@mui/icons-material/Download';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { useDataProvider, useNotify, Button } from 'react-admin';
import { format } from 'date-fns';
import { FuelDialog } from './FuelDialog';

interface FuelShowDialogProps {
  open: boolean;
  onClose: () => void;
  recordId: number;
  equipmentId: number;
}

const formatCurrency = (value: number | null | undefined) => {
  if (value == null) return '$0.00';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value);
};

const formatFileSize = (bytes?: number) => {
  if (!bytes) return 'Unknown size';
  const mb = bytes / (1024 * 1024);
  return mb < 1 ? `${(bytes / 1024).toFixed(1)} KB` : `${mb.toFixed(1)} MB`;
};

const fuelTypeLabels: Record<string, string> = {
  GASOLINE: 'Gasoline',
  DIESEL: 'Diesel',
  PROPANE: 'Propane',
  ELECTRIC: 'Electric',
  HYBRID: 'Hybrid'
};

export const FuelShowDialog: React.FC<FuelShowDialogProps> = ({
  open,
  onClose,
  recordId,
  equipmentId,
}) => {
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const [record, setRecord] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [files, setFiles] = useState<any[]>([]);

  useEffect(() => {
    if (open && recordId) {
      loadRecord();
    }
  }, [open, recordId]);

  const loadRecord = async () => {
    setLoading(true);
    try {
      const { data } = await dataProvider.getOne('equipment-fuel', { id: recordId });
      setRecord(data);
      setFiles(data.files || []);
    } catch (error) {
      console.error('Error loading fuel record:', error);
      notify('Error loading fuel record', { type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = () => {
    setEditDialogOpen(true);
  };

  const handleEditClose = () => {
    setEditDialogOpen(false);
    loadRecord(); // Reload to get updated data
  };

  const handleViewImage = (file: any) => {
    if (file.file?.url) {
      window.open(file.file.url, '_blank');
    }
  };

  const handleDownloadFile = (file: any) => {
    if (file.file?.url) {
      window.open(file.file.url, '_blank');
    }
  };

  if (!record) return null;

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            maxHeight: '90vh',
          },
        }}
      >
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          p: 2,
          borderBottom: '1px solid #e0e0e0'
        }}>
          <Typography variant="h5" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <LocalGasStationIcon color="primary" />
            Fuel Record Details
          </Typography>
          <Box>
            <IconButton onClick={handleEdit} color="primary" sx={{ mr: 1 }}>
              <EditIcon />
            </IconButton>
            <IconButton onClick={onClose}>
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>

        <DialogContent sx={{ p: 0 }}>
          {/* Main Information */}
          <Box sx={{ p: 3, borderBottom: '1px solid #e0e0e0' }}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Fuel Information
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <CalendarTodayIcon color="action" fontSize="small" />
                  <Box>
                    <Typography variant="caption" color="text.secondary">Fuel Date</Typography>
                    <Typography variant="body1">
                      {record.fuelDate ? format(new Date(record.fuelDate), 'PPP') : 'Not specified'}
                    </Typography>
                  </Box>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <LocalGasStationIcon color="action" fontSize="small" />
                  <Box>
                    <Typography variant="caption" color="text.secondary">Fuel Type</Typography>
                    <Typography variant="body1">
                      {fuelTypeLabels[record.fuelType] || record.fuelType || 'Not specified'}
                    </Typography>
                  </Box>
                </Box>
              </Grid>
              {record.mileage !== undefined && record.mileage !== null && (
                <Grid item xs={12} sm={6}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <SpeedIcon color="action" fontSize="small" />
                    <Box>
                      <Typography variant="caption" color="text.secondary">Odometer (KM)</Typography>
                      <Typography variant="body1">
                        {record.mileage.toLocaleString()} km
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
              )}
              {record.location && (
                <Grid item xs={12} sm={6}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <LocationOnIcon color="action" fontSize="small" />
                    <Box>
                      <Typography variant="caption" color="text.secondary">Location</Typography>
                      <Typography variant="body1">
                        {record.location}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
              )}
            </Grid>
          </Box>

          {/* Cost Information */}
          <Box sx={{ p: 3, borderBottom: '1px solid #e0e0e0' }}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Cost Details
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={4}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <AttachMoneyIcon color="action" fontSize="small" />
                  <Box>
                    <Typography variant="caption" color="text.secondary">Quantity</Typography>
                    <Typography variant="body1">
                      {record.quantity} gallons
                    </Typography>
                  </Box>
                </Box>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <AttachMoneyIcon color="action" fontSize="small" />
                  <Box>
                    <Typography variant="caption" color="text.secondary">Unit Cost</Typography>
                    <Typography variant="body1">
                      {formatCurrency(record.unitCost)}/gal
                    </Typography>
                  </Box>
                </Box>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <AttachMoneyIcon color="action" fontSize="small" />
                  <Box>
                    <Typography variant="caption" color="text.secondary">Total Cost</Typography>
                    <Typography variant="body1" fontWeight="bold" color="primary">
                      {formatCurrency(record.totalCost)}
                    </Typography>
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </Box>

          {/* File Attachments */}
          <Box sx={{ p: 3 }}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              File Attachments ({files.length})
            </Typography>
            
            {files.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                No files attached to this fuel record.
              </Typography>
            ) : (
              <List sx={{ mt: 2 }}>
                {files.map((file: any, index: number) => (
                  <ListItem
                    key={file.id || index}
                    sx={{
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 1,
                      mb: 1,
                      '&:last-child': { mb: 0 },
                      '&:hover': {
                        backgroundColor: 'action.hover',
                      },
                    }}
                  >
                    <ListItemIcon>
                      <AttachFileIcon />
                    </ListItemIcon>
                    <ListItemText
                      primary={file.file?.name || 'Unnamed file'}
                      secondary={formatFileSize(file.file?.size)}
                    />
                    <ListItemSecondaryAction>
                      {file.file?.mimeType?.startsWith('image/') && (
                        <IconButton
                          edge="end"
                          aria-label="view"
                          onClick={() => handleViewImage(file)}
                          sx={{ mr: 1 }}
                        >
                          <OpenInNewIcon />
                        </IconButton>
                      )}
                      <IconButton
                        edge="end"
                        aria-label="download"
                        onClick={() => handleDownloadFile(file)}
                      >
                        <DownloadIcon />
                      </IconButton>
                    </ListItemSecondaryAction>
                  </ListItem>
                ))}
              </List>
            )}
          </Box>
        </DialogContent>

        <DialogActions sx={{ p: 2, borderTop: '1px solid #e0e0e0' }}>
          <Button onClick={onClose}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Edit Dialog */}
      <FuelDialog
        open={editDialogOpen}
        onClose={handleEditClose}
        equipmentId={equipmentId}
        recordId={recordId}
        isEdit={true}
      />
    </>
  );
};